import { Box } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useGetAllEvents, UseGetAllEventsParams } from '../../../services/react-query/events';
import { UserSettingsCollapseKeys } from '../../../types/User';
import { Loading } from '../../../uikit';
import AccordionSection from '../../AccordionSection';
import PaginationBar from '../../PaginationBar';
import EventsTimelineTable from './EventTimelineTable';
import { ViewMode } from '../filters/EventsFilter';
import EventsTimelineHeader from './EventsTimelineHeader';
import { DateTime } from 'luxon';

interface IEventsTimelineProps {
  entityUuid: string;
  entityType: 'employees' | 'contacts' | 'account';
}

const EventsTimeline = ({ entityUuid, entityType }: IEventsTimelineProps) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.UPCOMING);

  const currentDate = useMemo(() => DateTime.now().startOf('day'), []);
  const queryParams: UseGetAllEventsParams = {
    page: currentPage,
    sort: {
      start_at: viewMode === ViewMode.UPCOMING ? 1 : -1,
    },
  };

  const filters = { [entityType]: entityUuid };
  if (viewMode === ViewMode.UPCOMING) {
    queryParams.filters = {
      ...filters,
      date_range: [currentDate.toISO(), null],
    };
  } else if (viewMode === ViewMode.PAST) {
    queryParams.filters = {
      ...filters,
      date_range: [null, currentDate.toISO()],
    };
  } else {
    queryParams.filters = filters;
  }

  const { data: eventsData, isLoading: isGetAllEventsLoading } = useGetAllEvents({
    params: queryParams,
  });

  const events = eventsData?.data || [];
  const meta = eventsData?.meta;

  const handlePaginationChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  return isGetAllEventsLoading ? (
    <Loading />
  ) : (
    <AccordionSection
      title="Events"
      collapseId={UserSettingsCollapseKeys.EMPLOYEE_ITEM_EVENTS_TIMELINE}
      headerElements={<EventsTimelineHeader entityType={entityType} viewMode={viewMode} onViewModeChange={setViewMode} />}
    >
      <Box sx={{ marginY: 1, width: '100%', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        <EventsTimelineTable timelineData={events} />
        <PaginationBar count={meta?.total_pages} page={currentPage} onChange={handlePaginationChange} sx={{ mt: 2 }} />
      </Box>
    </AccordionSection>
  );
};

export default EventsTimeline;
